import { useState, useCallback, useEffect } from 'react';
import type { FormEvent } from 'react';
import { X, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { ConnectionConfig, DriverType, SSLConfig, SSHConfig, TunnelConfig, TestConnectionResult } from '@/types';

// ═══════════════════════════════════════════
// Props
// ═══════════════════════════════════════════

interface ConnectionModalProps {
  connectionId?: string;
  initialData?: Partial<ConnectionConfig>;
  onClose: () => void;
  onSaved: () => void;
}

// ═══════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════

type TabId = 'general' | 'tunnel' | 'ssl' | 'advanced';

const TABS: { id: TabId; label: string }[] = [
  { id: 'general', label: 'General' },
  { id: 'tunnel', label: 'SSH Tunnel' },
  { id: 'ssl', label: 'SSL' },
  { id: 'advanced', label: 'Advanced' },
];

const DEFAULT_PORTS: Record<DriverType, number> = {
  postgresql: 5432,
  mysql: 3306,
  mssql: 1433,
  oracle: 1521,
  files: 0,
  mongodb: 27017,
  sqlite: 0,
};

const DRIVER_OPTIONS: { value: DriverType; label: string }[] = [
  { value: 'postgresql', label: 'PostgreSQL' },
  { value: 'mysql', label: 'MySQL' },
  { value: 'mssql', label: 'SQL Server' },
  { value: 'oracle', label: 'Oracle' },
  { value: 'mongodb', label: 'MongoDB' },
];

const SSL_MODES: SSLConfig['mode'][] = ['disable', 'require', 'verify-ca', 'verify-full'];

const COLOR_PRESETS = [
  '#4a7fff', '#3dd68c', '#f0c556', '#ef5350',
  '#b07fff', '#e0955c', '#4dd0c8', '#e06c75',
];

// ═══════════════════════════════════════════
// Form state
// ═══════════════════════════════════════════

interface FormState {
  name: string;
  driverType: DriverType;
  host: string;
  port: number;
  databaseName: string;
  username: string;
  password: string;
  sslConfig: SSLConfig;
  sshConfig: SSHConfig;
  tunnelId: string | null;
  colorTag: string | null;
}

function buildInitialState(initialData?: Partial<ConnectionConfig>): FormState {
  const driverType: DriverType = initialData?.driverType ?? 'postgresql';
  return {
    name: initialData?.name ?? '',
    driverType,
    host: initialData?.host ?? 'localhost',
    port: initialData?.port ?? DEFAULT_PORTS[driverType],
    databaseName: initialData?.databaseName ?? '',
    username: initialData?.username ?? '',
    password: initialData?.password ?? '',
    sslConfig: initialData?.sslConfig ?? { mode: 'disable' },
    sshConfig: initialData?.sshConfig ?? { enabled: false },
    tunnelId: initialData?.tunnelId ?? null,
    colorTag: initialData?.colorTag ?? null,
  };
}

// ═══════════════════════════════════════════
// Component
// ═══════════════════════════════════════════

export function ConnectionModal({ connectionId, initialData, onClose, onSaved }: ConnectionModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const [form, setForm] = useState<FormState>(() => buildInitialState(initialData));
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);
  const [error, setError] = useState('');

  const isEditing = !!connectionId;

  // Reset port when driver type changes (only if port matches old default)
  const handleDriverChange = useCallback((newDriver: DriverType) => {
    setForm(prev => {
      const oldDefault = DEFAULT_PORTS[prev.driverType];
      const newPort = prev.port === oldDefault ? DEFAULT_PORTS[newDriver] : prev.port;
      return { ...prev, driverType: newDriver, port: newPort };
    });
  }, []);

  // Generic field updater
  const updateField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setTestResult(null);
  }, []);

  const updateSSL = useCallback(<K extends keyof SSLConfig>(key: K, value: SSLConfig[K]) => {
    setForm(prev => ({ ...prev, sslConfig: { ...prev.sslConfig, [key]: value } }));
    setTestResult(null);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Build payload for API
  const buildPayload = useCallback(() => {
    return {
      name: form.name,
      driverType: form.driverType,
      host: form.host,
      port: form.port,
      databaseName: form.databaseName,
      username: form.username,
      password: form.password || undefined,
      sslConfig: form.sslConfig,
      sshConfig: form.sshConfig,
      tunnelId: form.tunnelId,
      colorTag: form.colorTag,
    };
  }, [form]);

  // Test connection.
  //
  // When editing an existing connection we include `connectionId` so the
  // server can fall back to the stored password if the user hasn't retyped
  // one (we never send passwords back to the client, so on re-open the
  // field is always empty and Test would otherwise fail with an auth error).
  const handleTest = useCallback(async () => {
    setTesting(true);
    setTestResult(null);
    setError('');
    try {
      const payload = { ...buildPayload(), connectionId: connectionId || undefined };
      const result = await apiClient.post<TestConnectionResult>('/api/connections/test', payload);
      setTestResult(result);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Test failed';
      setTestResult({ ok: false, error: message, latencyMs: 0 });
    } finally {
      setTesting(false);
    }
  }, [buildPayload, connectionId]);

  // Save connection
  const handleSave = useCallback(async (e: FormEvent) => {
    e.preventDefault();

    if (!form.name.trim()) {
      setError('Connection name is required');
      setActiveTab('general');
      return;
    }
    if (!form.host.trim()) {
      setError('Host is required');
      setActiveTab('general');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = buildPayload();
      if (isEditing) {
        await apiClient.put(`/api/connections/${connectionId}`, payload);
      } else {
        await apiClient.post('/api/connections', payload);
      }
      onSaved();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save connection';
      setError(message);
    } finally {
      setSaving(false);
    }
  }, [form, isEditing, connectionId, buildPayload, onSaved]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/60 backdrop-blur-sm sm:items-center"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <form
        onSubmit={handleSave}
        className="w-full flex flex-col bg-dbgo-modal border shadow-2xl animate-fade-in sm:w-[580px] sm:max-h-[80vh] sm:rounded-lg"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-0">
          <h2 className="text-sm font-semibold text-text-primary">
            {isEditing ? 'Edit Connection' : 'New Connection'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex bg-dbgo-panel mx-5 mt-3 rounded-sm overflow-hidden">
          {TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex-1 text-xs font-medium py-2 transition-colors relative
                ${activeTab === tab.id
                  ? 'text-text-primary'
                  : 'text-text-muted hover:text-text-secondary'
                }
              `}
            >
              {tab.label}
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent-blue" />
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
          {activeTab === 'general' && (
            <GeneralTab form={form} onFieldChange={updateField} onDriverChange={handleDriverChange} isEditing={isEditing} />
          )}
          {activeTab === 'tunnel' && (
            <TunnelSelectorTab
              tunnelId={form.tunnelId}
              onTunnelChange={(id) => updateField('tunnelId', id)}
            />
          )}
          {activeTab === 'ssl' && (
            <SSLTab sslConfig={form.sslConfig} onChange={updateSSL} />
          )}
          {activeTab === 'advanced' && (
            <AdvancedTab colorTag={form.colorTag} onColorChange={(c) => updateField('colorTag', c)} />
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="px-5 pb-2 text-xs text-accent-red">{error}</div>
        )}

        {/* Test result */}
        {testResult && (
          <div className={`mx-5 mb-2 flex items-center gap-2 px-3 py-2 rounded-sm text-xs ${testResult.ok ? 'bg-accent-green/10 text-accent-green' : 'bg-accent-red/10 text-accent-red'}`}>
            {testResult.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
            <span>
              {testResult.ok
                ? `Connected successfully (${testResult.latencyMs}ms)`
                : testResult.error || 'Connection failed'}
            </span>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t">
          <button
            type="button"
            onClick={handleTest}
            disabled={testing || saving}
            className="flex items-center gap-1.5 px-3 py-[6px] text-xs font-medium text-accent-green border border-accent-green rounded-sm hover:bg-accent-green/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {testing && <Loader2 size={12} className="animate-spin" />}
            {testing ? 'Testing...' : 'Test Connection'}
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-3 py-[6px] text-xs font-medium text-text-secondary hover:text-text-primary rounded-sm hover:bg-dbgo-tab-hover transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-[6px] text-xs font-medium text-white bg-accent-blue hover:bg-accent-blue/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm transition-colors"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// ═══════════════════════════════════════════
// Sub-components: Reusable form primitives
// ═══════════════════════════════════════════

const inputClass =
  'w-full bg-dbgo-input border rounded-sm px-2.5 py-[7px] text-[13px] text-text-primary placeholder:text-text-muted outline-none focus:border-focus transition-colors';

const labelClass = 'block text-xs font-semibold text-text-secondary mb-1.5';

const selectClass =
  'w-full bg-dbgo-input border rounded-sm px-2.5 py-[7px] text-[13px] text-text-primary outline-none focus:border-focus transition-colors appearance-none cursor-pointer';

function FormField({ label, htmlFor, children }: { label: string; htmlFor?: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={htmlFor} className={labelClass}>{label}</label>
      {children}
    </div>
  );
}

function FormRow({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-3">{children}</div>;
}

// ═══════════════════════════════════════════
// General Tab
// ═══════════════════════════════════════════

interface GeneralTabProps {
  form: FormState;
  onFieldChange: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  onDriverChange: (driver: DriverType) => void;
  isEditing: boolean;
}

function GeneralTab({ form, onFieldChange, onDriverChange, isEditing }: GeneralTabProps) {
  return (
    <div className="flex flex-col gap-3">
      <FormField label="Name" htmlFor="conn-name">
        <input
          id="conn-name"
          type="text"
          value={form.name}
          onChange={e => onFieldChange('name', e.target.value)}
          placeholder="My Database"
          autoFocus
          className={inputClass}
        />
      </FormField>

      <FormField label="Driver Type" htmlFor="conn-driver">
        <select
          id="conn-driver"
          value={form.driverType}
          onChange={e => onDriverChange(e.target.value as DriverType)}
          className={selectClass}
        >
          {DRIVER_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </FormField>

      <FormRow>
        <FormField label={form.driverType === 'mssql' ? 'Server' : 'Host'} htmlFor="conn-host">
          <input
            id="conn-host"
            type="text"
            value={form.host}
            onChange={e => onFieldChange('host', e.target.value)}
            placeholder={form.driverType === 'mssql' ? 'localhost\\SQLEXPRESS' : 'localhost'}
            className={inputClass}
          />
        </FormField>
        <FormField label="Port" htmlFor="conn-port">
          <input
            id="conn-port"
            type="number"
            value={form.port}
            onChange={e => onFieldChange('port', parseInt(e.target.value, 10) || 0)}
            className={inputClass}
          />
        </FormField>
      </FormRow>

      <FormField
        label={form.driverType === 'oracle' ? 'Service Name' : 'Database'}
        htmlFor="conn-database"
      >
        <input
          id="conn-database"
          type="text"
          value={form.databaseName}
          onChange={e => onFieldChange('databaseName', e.target.value)}
          placeholder={
            form.driverType === 'postgresql' ? 'postgres'
            : form.driverType === 'mysql' ? 'mysql'
            : form.driverType === 'mssql' ? 'master'
            : form.driverType === 'oracle' ? 'ORCL'
            : 'database'
          }
          className={inputClass}
        />
        {form.driverType === 'oracle' && (
          <p className="text-[10px] text-text-muted mt-1">
            Oracle connects using host:port/service_name format. Enter the service name (not SID).
          </p>
        )}
      </FormField>

      {form.driverType === 'mssql' && (
        <FormRow>
          <FormField label="Encrypt" htmlFor="conn-mssql-encrypt">
            <div className="flex items-center gap-2 h-[34px]">
              <button
                type="button"
                id="conn-mssql-encrypt"
                role="switch"
                aria-checked={form.sslConfig.mode !== 'disable'}
                onClick={() => onFieldChange('sslConfig', {
                  ...form.sslConfig,
                  mode: form.sslConfig.mode === 'disable' ? 'require' : 'disable',
                })}
                className={`w-9 h-5 rounded-full transition-colors flex-shrink-0 ${
                  form.sslConfig.mode !== 'disable' ? 'bg-accent-blue' : 'bg-text-muted/30'
                }`}
              >
                <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${
                  form.sslConfig.mode !== 'disable' ? 'translate-x-[18px]' : 'translate-x-[2px]'
                }`} />
              </button>
              <span className="text-xs text-text-secondary">
                {form.sslConfig.mode !== 'disable' ? 'Encrypted' : 'Unencrypted'}
              </span>
            </div>
          </FormField>
          <FormField label="Trust Server Certificate" htmlFor="conn-mssql-trust">
            <div className="flex items-center gap-2 h-[34px]">
              <button
                type="button"
                id="conn-mssql-trust"
                role="switch"
                aria-checked={form.sslConfig.mode !== 'verify-full'}
                onClick={() => onFieldChange('sslConfig', {
                  ...form.sslConfig,
                  mode: form.sslConfig.mode === 'verify-full' ? 'require' : 'verify-full',
                })}
                className={`w-9 h-5 rounded-full transition-colors flex-shrink-0 ${
                  form.sslConfig.mode !== 'verify-full' ? 'bg-accent-blue' : 'bg-text-muted/30'
                }`}
              >
                <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${
                  form.sslConfig.mode !== 'verify-full' ? 'translate-x-[18px]' : 'translate-x-[2px]'
                }`} />
              </button>
              <span className="text-xs text-text-secondary">
                {form.sslConfig.mode !== 'verify-full' ? 'Trust' : 'Verify'}
              </span>
            </div>
          </FormField>
        </FormRow>
      )}

      <FormRow>
        <FormField label="Username" htmlFor="conn-username">
          <input
            id="conn-username"
            type="text"
            value={form.username}
            onChange={e => onFieldChange('username', e.target.value)}
            placeholder="postgres"
            className={inputClass}
          />
        </FormField>
        <FormField label="Password" htmlFor="conn-password">
          <input
            id="conn-password"
            type="password"
            value={form.password}
            onChange={e => onFieldChange('password', e.target.value)}
            placeholder={isEditing ? 'stored — leave blank to keep' : '••••••••'}
            className={inputClass}
          />
        </FormField>
      </FormRow>
    </div>
  );
}

// ═══════════════════════════════════════════
// Tunnel Selector Tab
// ═══════════════════════════════════════════

interface TunnelSelectorTabProps {
  tunnelId: string | null;
  onTunnelChange: (tunnelId: string | null) => void;
}

function TunnelSelectorTab({ tunnelId, onTunnelChange }: TunnelSelectorTabProps) {
  const { data: tunnels = [], isLoading } = useQuery<TunnelConfig[]>({
    queryKey: ['tunnels'],
    queryFn: () => apiClient.get<TunnelConfig[]>('/api/tunnels'),
  });

  const selectedTunnel = tunnels.find(t => t.id === tunnelId);

  return (
    <div className="flex flex-col gap-3">
      <FormField label="SSH Tunnel" htmlFor="conn-tunnel">
        <select
          id="conn-tunnel"
          value={tunnelId ?? ''}
          onChange={e => onTunnelChange(e.target.value || null)}
          className={selectClass}
          disabled={isLoading}
        >
          <option value="">None (direct connection)</option>
          {tunnels.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </FormField>

      {selectedTunnel && (
        <div className="bg-dbgo-panel border rounded-md p-3 text-xs">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-text-muted">SSH Host</span>
              <p className="text-text-primary font-mono">{selectedTunnel.host}:{selectedTunnel.port}</p>
            </div>
            <div>
              <span className="text-text-muted">Username</span>
              <p className="text-text-primary font-mono">{selectedTunnel.username}</p>
            </div>
            <div>
              <span className="text-text-muted">Auth</span>
              <p className="text-text-primary">
                {selectedTunnel.authMethod === 'key' ? 'Private Key' : selectedTunnel.authMethod === 'password' ? 'Password' : 'SSH Agent'}
              </p>
            </div>
          </div>
        </div>
      )}

      {!tunnelId && (
        <p className="text-xs text-text-muted mt-1">
          Select a tunnel to route this connection through, or leave as &quot;None&quot; for a direct connection.
          Manage tunnels from the sidebar.
        </p>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
// SSL Tab
// ═══════════════════════════════════════════

interface SSLTabProps {
  sslConfig: SSLConfig;
  onChange: <K extends keyof SSLConfig>(key: K, value: SSLConfig[K]) => void;
}

function SSLTab({ sslConfig, onChange }: SSLTabProps) {
  const showCa = sslConfig.mode !== 'disable';
  const showClient = sslConfig.mode === 'verify-ca' || sslConfig.mode === 'verify-full';

  return (
    <div className="flex flex-col gap-3">
      <FormField label="SSL Mode" htmlFor="ssl-mode">
        <select
          id="ssl-mode"
          value={sslConfig.mode}
          onChange={e => onChange('mode', e.target.value as SSLConfig['mode'])}
          className={selectClass}
        >
          {SSL_MODES.map(mode => (
            <option key={mode} value={mode}>
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </option>
          ))}
        </select>
      </FormField>

      {showCa && (
        <FormField label="CA Certificate Path" htmlFor="ssl-ca">
          <input
            id="ssl-ca"
            type="text"
            value={sslConfig.caCertPath ?? ''}
            onChange={e => onChange('caCertPath', e.target.value)}
            placeholder="/path/to/ca.pem"
            className={inputClass}
          />
        </FormField>
      )}

      {showClient && (
        <>
          <FormField label="Client Certificate Path" htmlFor="ssl-cert">
            <input
              id="ssl-cert"
              type="text"
              value={sslConfig.clientCertPath ?? ''}
              onChange={e => onChange('clientCertPath', e.target.value)}
              placeholder="/path/to/client-cert.pem"
              className={inputClass}
            />
          </FormField>
          <FormField label="Client Key Path" htmlFor="ssl-key">
            <input
              id="ssl-key"
              type="text"
              value={sslConfig.clientKeyPath ?? ''}
              onChange={e => onChange('clientKeyPath', e.target.value)}
              placeholder="/path/to/client-key.pem"
              className={inputClass}
            />
          </FormField>
        </>
      )}

      {sslConfig.mode === 'disable' && (
        <p className="text-xs text-text-muted mt-1">
          SSL is disabled. Select a mode to configure secure connections.
        </p>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
// Advanced Tab
// ═══════════════════════════════════════════

interface AdvancedTabProps {
  colorTag: string | null;
  onColorChange: (color: string | null) => void;
}

function AdvancedTab({ colorTag, onColorChange }: AdvancedTabProps) {
  return (
    <div className="flex flex-col gap-3">
      <FormField label="Color Tag">
        <div className="flex items-center gap-2 mt-1">
          {/* No color option */}
          <button
            type="button"
            onClick={() => onColorChange(null)}
            className={`w-6 h-6 rounded-full border-2 transition-colors flex items-center justify-center ${
              colorTag === null ? 'border-text-primary' : 'border-strong hover:border-text-muted'
            }`}
            title="No color"
          >
            {colorTag === null && (
              <span className="w-2 h-2 rounded-full bg-text-primary" />
            )}
          </button>
          {/* Preset colors */}
          {COLOR_PRESETS.map(color => (
            <button
              key={color}
              type="button"
              onClick={() => onColorChange(color)}
              className={`w-6 h-6 rounded-full border-2 transition-colors ${
                colorTag === color ? 'border-text-primary scale-110' : 'border-transparent hover:border-strong'
              }`}
              style={{ backgroundColor: color }}
              title={color}
            />
          ))}
        </div>
      </FormField>

      <p className="text-xs text-text-muted mt-2">
        More advanced options will be available in a future release.
      </p>
    </div>
  );
}

